import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log, error as logError } from '../utils/DebugLogger';

import '../contracts/SetupFee_ComprovantesResponse';
import '../contracts/SetupFee_ComprovantesEmAnaliseResponse';
import '../contracts/SetupFee_ComprovanteInput';
import '../contracts/SetupFee_ComprovanteRevisarInput';
import '../contracts/SetupFee_ComprovanteArquivoResponse';

import type { SetupFee_ComprovantesResponseDados } from '../contracts/SetupFee_ComprovantesResponse';
import type { SetupFee_ComprovanteRow } from '../contracts/SetupFee_ComprovanteRow';

/**
 * Taxa de implantação paga por PIX ou transferência, com comprovante (chamado New Shoes
 * 2026-0079, F3). O franqueado manda; o Full aprova (o setup vira pago) ou recusa com motivo.
 * As regras (um em análise por vez, prazo da COF, valor e data) moram no app-api.
 */
const URL_LISTA = '/index.php/subscriptions/setup-fee/comprovantes';
const URL_ENVIAR = '/index.php/subscriptions/setup-fee/comprovante';
const URL_ARQUIVO = '/index.php/subscriptions/setup-fee/comprovante-arquivo';
const URL_REVISAR = '/index.php/subscriptions/setup-fee/comprovante-revisar';
const URL_FILA = '/index.php/subscriptions/setup-fee/comprovantes-em-analise';

export const TAMANHO_MAX_COMPROVANTE = 10 * 1024 * 1024;

const MOTIVOS: Record<string, string> = {
  setup_ja_quitado: 'A taxa de implantação já está quitada.',
  comprovante_em_analise: 'Já há um comprovante em análise.',
  cof_menos_de_10_dias: 'A taxa só pode ser paga 10 dias depois da entrega da COF (Lei 13.966/2019).',
  valor_invalido: 'Informe o valor pago.',
  data_do_pagamento_invalida: 'Informe a data do pagamento.',
  data_do_pagamento_no_futuro: 'A data do pagamento não pode ser no futuro.',
  arquivo_deve_ser_pdf_ou_imagem: 'Envie um PDF ou uma imagem (JPG, PNG ou WebP).',
  arquivo_maior_que_10mb: 'O arquivo passa de 10 MB.',
  motivo_obrigatorio: 'Escreva o motivo.',
  comprovante_ja_revisado: 'Este comprovante já foi conferido.',
  reexija_antes_de_marcar_pago: 'A taxa desta franquia está isenta.',
  franquia_fora_do_escopo: 'Esta franquia não é sua.',
  falha_ao_enviar_arquivo: 'Não foi possível guardar o arquivo. Tente de novo.',
};

export const mensagemDoComprovante = (e: unknown): string => {
  const bruto = e instanceof Error ? e.message : String(e);
  return MOTIVOS[bruto] ?? bruto;
};

export function useSetupComprovantes() {
  const situacao = ref<SetupFee_ComprovantesResponseDados | null>(null);
  const fila = ref<SetupFee_ComprovanteRow[]>([]);

  const executar = async <T>(onde: string, fn: () => Promise<T>): Promise<T> => {
    try {
      return await fn();
    } catch (e) {
      logError('useSetupComprovantes', onde, 'Erro', e);
      throw e;
    }
  };

  const carregar = (franquiaId: number): Promise<void> =>
    executar('carregar', async () => {
      const r = await httpService.get(`${URL_LISTA}/${franquiaId}`, { responseContract: 'SetupFee_ComprovantesResponse' });
      if (!r.success || !r.dados) throw new Error(r.error || 'Erro ao carregar os comprovantes');
      situacao.value = r.dados;
      log('useSetupComprovantes', 'carregar', 'comprovantes', r.dados.comprovantes.length);
    });

  /** O franqueado manda o comprovante; o valor vai como a pessoa digitou ("1.500,00"). */
  const enviar = (franquiaId: number, valor: string, pagoEm: string, arquivo: File): Promise<void> =>
    executar('enviar', async () => {
      if (arquivo.size > TAMANHO_MAX_COMPROVANTE) throw new Error('arquivo_maior_que_10mb');
      const formulario = new FormData();
      formulario.append('franquia_id', String(franquiaId));
      formulario.append('valor', valor);
      formulario.append('pago_em', pagoEm);
      formulario.append('arquivo', arquivo);
      // inputContract declarado mesmo sendo multipart: amarra a chamada ao contrato do backend.
      const r = await httpService.post(URL_ENVIAR, formulario, { inputContract: 'SetupFee_ComprovanteInput', responseContract: 'SetupFee_ComprovantesResponse' });
      if (!r.success || !r.dados) throw new Error(r.error || 'Erro ao enviar o comprovante');
      situacao.value = r.dados;
    });

  const linkDoArquivo = (id: number): Promise<string> =>
    executar('linkDoArquivo', async () => {
      const r = await httpService.get(`${URL_ARQUIVO}/${id}`, { responseContract: 'SetupFee_ComprovanteArquivoResponse' });
      if (!r.success || !r.dados?.url) throw new Error(r.error || 'Erro ao abrir o comprovante');
      return r.dados.url;
    });

  /** Full: aprovar (o setup vira pago) ou recusar com motivo. */
  const revisar = (comprovanteId: number, acao: 'aprovar' | 'recusar', motivo?: string): Promise<void> =>
    executar('revisar', async () => {
      const r = await httpService.post(URL_REVISAR, { comprovante_id: comprovanteId, acao, ...(motivo !== undefined ? { motivo } : {}) }, {
        inputContract: 'SetupFee_ComprovanteRevisarInput',
        responseContract: 'SetupFee_ComprovantesResponse',
      });
      if (!r.success || !r.dados) throw new Error(r.error || 'Erro ao conferir o comprovante');
      situacao.value = r.dados;
      fila.value = fila.value.filter((c) => c.id !== comprovanteId);
    });

  /** Full: tudo o que espera conferência. */
  const carregarFila = (): Promise<void> =>
    executar('carregarFila', async () => {
      const r = await httpService.get(URL_FILA, { responseContract: 'SetupFee_ComprovantesEmAnaliseResponse' });
      if (!r.success || !r.dados) throw new Error(r.error || 'Erro ao carregar os comprovantes');
      fila.value = r.dados.comprovantes;
    });

  return { situacao, fila, carregar, enviar, linkDoArquivo, revisar, carregarFila };
}
